import { NavLink } from 'libs/router'
import { useTranslation } from 'libs/i18n'
import { format, parseISO } from 'libs/dates'
import { ContextMenu, Currency } from 'shared/components'
import { BooleanBadge, Detail } from 'shared/atoms'
import { useUserRoles } from 'shared/hooks'
import type { Option } from 'shared/components/ContextMenu/types'
import { ActivityStatus } from '../ActivityStatus/ActivityStatus'
import { BookingStatus } from '../BookingStatus/BookingStatus'
import { PaymentStatus } from '../PaymentStatus/PaymentStatus'
import { useBookingPath } from '../../hooks/useContextMenuOptions'
import type { BookingItem } from '../../types/bookings.types'

export type BookingProps = {
	booking: BookingItem
	menuOptions: Option[]
}

const date = (value: string) => format(new Date(value), 'dd.MM.yyyy')

export const Booking = ({ booking, menuOptions }: BookingProps) => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const bookingPath = useBookingPath()
	const {
		id,
		ref_nr,
		created,
		is_active,
		status,
		user,
		booth,
		rental,
		start_date,
		end_date,
		payment_status,
		payment_method,
		sold_quantity,
		sold_total,
		remaining_products,
		total_value,
		lifecycle,
		store_notes
	} = booking

	return (
		<li className="p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<NavLink
						className="text-[14px] font-bold tabular-nums text-primary hover:underline"
						to={bookingPath(id)}>
						{ref_nr}
					</NavLink>
					<p className="text-[11px] tabular-nums text-muted-foreground">
						{format(parseISO(String(created)), 'dd.MM.yyyy HH:mm:ss')}
					</p>
				</div>
				<div className="flex shrink-0 items-start gap-2">
					<div className="flex flex-col items-end gap-1.5">
						{isStaff && <ActivityStatus status={is_active} prefix={t('Status')} />}
						<BookingStatus status={status} prefix={t('Booking status')} />
					</div>
					<ContextMenu
						className="-mr-2 -mt-1"
						dataId={id}
						item={booking}
						options={menuOptions}
						label={t('Actions for booking {{ id }}', { id: ref_nr })}
					/>
				</div>
			</div>
			<dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
				{isStaff && (
					<Detail
						className="col-span-2"
						label={t('User')}
						value={
							<>
								{user?.name}{' '}
								<span className="font-normal text-muted-foreground">{user?.email}</span>
							</>
						}
					/>
				)}
				<Detail className="col-span-2" label={t('Type')} value={booth?.booth_type?.name || '—'} />
				<Detail label={t('Area')} value={booth?.number ? `#${booth.number}` : '—'} />
				<Detail
					label={t('Days')}
					value={typeof rental !== 'number' && rental?.duration != null ? rental.duration : '—'}
				/>
				<Detail label={t('From')} value={date(start_date)} />
				<Detail label={t('To')} value={date(end_date)} />
				<div>
					<dt className="font-medium uppercase text-muted-foreground">{t('Payment')}</dt>
					<dd className="mt-1">
						<PaymentStatus status={payment_status} />
					</dd>
				</div>
				<Detail label={t('Method')} value={payment_method || '—'} />
				<Detail label={t('Products sold')} value={sold_quantity} />
				<Detail
					label={t('Amount sold')}
					value={<Currency price={{ amount: +sold_total, currency: total_value.currency }} />}
				/>
				<Detail label={t('Products remaining')} value={remaining_products} />
				<Detail label={t('Amount remaining')} value={<Currency price={total_value} />} />
				{isStaff && (
					<>
						<div>
							<dt className="font-medium uppercase text-muted-foreground">
								{t('Takedown booked')}
							</dt>
							<dd className="mt-1">
								<BooleanBadge
									size="sm"
									value={!!lifecycle?.customer_notified_takedown_at}
									label={t('Takedown booked')}
								/>
							</dd>
						</div>
						<div>
							<dt className="font-medium uppercase text-muted-foreground">{t('Notes')}</dt>
							<dd className="mt-1">
								<BooleanBadge size="sm" value={!!store_notes} label={t('Notes')} />
							</dd>
						</div>
					</>
				)}
			</dl>
		</li>
	)
}
